import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  StackDivider,
  Text,
  TextProps,
  Tooltip,
  TooltipProps,
  VStack,
} from '@chakra-ui/react'
import { getDateString, WithId } from '@chem-po/core'
import { formatValue, useScreen } from '@hb/react'
import {
  Assessment,
  deliveryLocationField,
  deliveryTypesField,
  FieldMapValue,
  mergeData,
  newbornSexField,
  poundsToLbOz,
  ValidationErrors,
} from '@hb/shared'
import { additionalPregnanciesField, existingPregnancyField } from '@hb/shared/src/fields/gyn'
import { pregnancyOutcomeField } from '@hb/shared/src/fields/pregnancy'
import React, { useCallback, useMemo, useState } from 'react'
import { useFormState } from 'react-final-form'
import { useMyAssessments } from '../../contexts/user/UserAssessmentsContext'
import { ActionButton, DeleteButton, SolidActionButton } from '../Buttons'
import { overflowBody } from '../forms/FinalForm/constants'
import { SimpleForm } from '../forms/FinalForm/SimpleForm'
import { RenderStageProps } from '../forms/renderStage/types'
import { DefaultModal } from '../Modals'
import { useGynCareView } from './context'

const ExistingPregnancyDataLabel = ({
  value,
  textProps,
  bg = 'white',
  onViewMore,
  viewingMore,
}: {
  value: string
  textProps?: TextProps
  bg?: string
  onViewMore?: () => void
  viewingMore?: boolean
}) => (
  <Flex
    justifyContent="center"
    bg={bg}
    flexFlow="column"
    overflow="hidden"
    h="100%"
    w="100%"
    py={1}
    px={2}>
    <Text fontSize="sm" fontFamily="Open Sans" {...textProps}>
      {value}
    </Text>
    {onViewMore ? (
      <Button
        variant="link"
        fontSize="sm"
        w="100%"
        textAlign="left"
        justifyContent="flex-start"
        fontFamily="Open Sans"
        color="green.500"
        onClick={onViewMore}>
        {viewingMore ? '- DETAILS' : '+ DETAILS'}
      </Button>
    ) : null}
  </Flex>
)

const getRowBg = (index: number) => (index % 2 === 0 ? 'gray.50' : 'white')

const previousPregnancyNumColumns = 10

const useChildGestationAndBirthRows = (
  childsGestationAndBirth: FieldMapValue | null | undefined,
) => {
  const {
    problemsDuringPregnancy,
    problemsDuringPregnancyDetails,
    treatmentsDuringPregnancy,
    treatmentsDuringPregnancyDetails,
    drugOrAlcoholUseDuringPregnancy,
    drugOrAlcoholUseDuringPregnancyDetails,
    complicationsDuringBirth,
    complicationsDuringBirthDetails,
    complicationsDuringInfancy,
    complicationsDuringInfancyDetails,
  } = childsGestationAndBirth ?? {}

  return useMemo(() => {
    const rows: { label: string; value: string }[][] = []

    const addRow = (label: string, value: string) => {
      const currRow = rows[rows.length - 1]
      if (!currRow || currRow.length > 2) {
        rows.push([{ label, value }])
      } else {
        currRow.push({ label, value })
      }
    }

    if (problemsDuringPregnancy)
      addRow(
        'Problems or illnesses during pregnancy',
        problemsDuringPregnancyDetails ?? 'No details provided',
      )
    if (treatmentsDuringPregnancy)
      addRow(
        'Medications, treatments, or interventions during pregnancy',
        treatmentsDuringPregnancyDetails ?? 'No details provided',
      )
    if (drugOrAlcoholUseDuringPregnancy)
      addRow(
        'Drug or alcohol use during pregnancy',
        drugOrAlcoholUseDuringPregnancyDetails ?? 'No details provided',
      )
    if (complicationsDuringBirth)
      addRow(
        "Complications during child's birth",
        complicationsDuringBirthDetails ?? 'No details provided',
      )
    if (complicationsDuringInfancy)
      addRow(
        "Complications during this child's infancy",
        complicationsDuringInfancyDetails ?? 'No details provided',
      )
    return rows
  }, [
    problemsDuringPregnancy,
    treatmentsDuringPregnancy,
    drugOrAlcoholUseDuringPregnancy,
    complicationsDuringBirth,
    complicationsDuringInfancy,
    problemsDuringPregnancyDetails,
    treatmentsDuringPregnancyDetails,
    drugOrAlcoholUseDuringPregnancyDetails,
    complicationsDuringBirthDetails,
    complicationsDuringInfancyDetails,
  ])
}

const ChildGestationAndBirthDetailView = ({
  bg,
  label,
  value,
}: {
  bg: string
  label: string
  value: string
}) => (
  <Box bg={bg} w="100%" p={2}>
    <Text fontSize="sm" fontFamily="Open Sans" fontWeight={600}>
      {label}
    </Text>
    <Text fontSize="sm" fontFamily="Open Sans">
      {value}
    </Text>
  </Box>
)

const ChildGestationAndBirthView = ({
  bg,
  rows,
}: {
  bg: string
  rows: { label: string; value: string }[][]
}) => (
  <Box>
    {rows.map((row, index) => (
      <HStack
        key={index}
        alignItems="flex-start"
        spacing={0}
        borderBottom={index < rows.length - 1 ? '1px solid #cdcdcd' : 'none'}
        w="100%"
        bg={bg}
        divider={<StackDivider />}>
        {row.map((item, index) => (
          <ChildGestationAndBirthDetailView
            key={index}
            bg={bg}
            label={item.label}
            value={item.value}
          />
        ))}
      </HStack>
    ))}
  </Box>
)

const ExistingPregnancyView = ({
  pregnancy,
  index,
  inEditModal = false,
  onEditClick,
}: {
  pregnancy: WithId<Assessment>
  index: number
  inEditModal?: boolean
  onEditClick?: () => void
}) => {
  const { admin } = useGynCareView()
  const { delivery, data, corrections } = pregnancy
  const [viewingMore, setViewingMore] = useState(false)
  const bg = getRowBg(index)
  const childsGestationAndBirth = useMemo(
    () =>
      admin
        ? mergeData(data?.childsGestationAndBirth, corrections?.childsGestationAndBirth)
        : data?.childsGestationAndBirth,
    [admin, corrections, data],
  )

  const pregnancyOutcome = useMemo(
    () =>
      admin ? (corrections?.pregnancyOutcome ?? data?.pregnancyOutcome) : data?.pregnancyOutcome,
    [admin, corrections, data],
  )
  const pregnancyOutcomeOther = useMemo(
    () =>
      admin
        ? (corrections?.pregnancyOutcomeOther ?? data?.pregnancyOutcomeOther)
        : data?.pregnancyOutcomeOther,
    [admin, corrections, data],
  )

  const childGestationAndBirthRows = useChildGestationAndBirthRows(childsGestationAndBirth)

  const lmp = corrections?.delivery?.lmp ?? data?.delivery?.lmp
  const {
    newbornFname,
    newbornLname,
    newbornSex,
    newbornWeight,
    deliveredOn,
    location,
    locationOther,
    deliveryTypes,
  } = delivery ?? {}

  const weightString = useMemo(() => {
    if (!newbornWeight) return 'None'
    const { lb, oz } = poundsToLbOz(newbornWeight)
    return `${lb} lb, ${oz} oz`
  }, [newbornWeight])

  const locationString = useMemo(() => {
    if (!location) return 'None'
    if (location === 'other') return `Other - ${locationOther}`
    return formatValue({ field: deliveryLocationField, value: location })
  }, [location, locationOther])

  const nameString = useMemo(() => {
    if (!newbornFname && !newbornLname) return 'None'
    return `${newbornFname} ${newbornLname}`
  }, [newbornFname, newbornLname])
  const formattedPregnancyOutcome = useMemo(() => {
    if (!pregnancyOutcome) return null
    if (pregnancyOutcome === 'other') return `Other - ${pregnancyOutcomeOther}`
    return formatValue({ field: pregnancyOutcomeField, value: pregnancyOutcome })
  }, [pregnancyOutcome, pregnancyOutcomeOther])

  return (
    <>
      {inEditModal ? null : (
        <GridItem rowSpan={viewingMore ? childGestationAndBirthRows.length + 1 : 1}>
          <ExistingPregnancyDataLabel
            bg={bg}
            textProps={{ fontWeight: 600 }}
            value={`${index + 1}`}
          />
        </GridItem>
      )}
      {inEditModal ? null : (
        <ExistingPregnancyDataLabel
          bg={bg}
          onViewMore={
            childGestationAndBirthRows.length ? () => setViewingMore(!viewingMore) : undefined
          }
          viewingMore={viewingMore}
          textProps={{
            color: formattedPregnancyOutcome ? undefined : 'red.600',
            fontWeight: formattedPregnancyOutcome ? undefined : 600,
          }}
          value={formattedPregnancyOutcome ?? 'Required'}
        />
      )}
      <ExistingPregnancyDataLabel
        bg={bg}
        value={formatValue({ field: deliveryTypesField, value: deliveryTypes })}
      />
      <ExistingPregnancyDataLabel bg={bg} value={locationString} />
      <ExistingPregnancyDataLabel bg={bg} value={nameString} />
      <ExistingPregnancyDataLabel
        bg={bg}
        value={deliveredOn ? getDateString(deliveredOn, 'short', true) : 'None'}
      />
      <ExistingPregnancyDataLabel
        bg={bg}
        value={lmp ? getDateString(lmp, 'short', true) : 'None'}
      />
      <ExistingPregnancyDataLabel
        bg={bg}
        value={formatValue({ field: newbornSexField, value: newbornSex })}
      />
      <ExistingPregnancyDataLabel bg={bg} value={weightString} />
      {inEditModal ? null : (
        <GridItem rowSpan={viewingMore ? childGestationAndBirthRows.length + 1 : 1}>
          <Center p={2} h="100%" w="100%" bg={bg}>
            {pregnancyOutcome ? (
              <ActionButton leftIcon={<EditIcon />} size="sm" onClick={onEditClick}>
                EDIT
              </ActionButton>
            ) : (
              <SolidActionButton size="sm" onClick={onEditClick}>
                COMPLETE
              </SolidActionButton>
            )}
          </Center>
        </GridItem>
      )}
      {!inEditModal && viewingMore && childGestationAndBirthRows.length ? (
        <GridItem colSpan={previousPregnancyNumColumns - 2}>
          <ChildGestationAndBirthView bg={bg} rows={childGestationAndBirthRows} />
        </GridItem>
      ) : null}
    </>
  )
}

const deleteTooltipProps: Partial<TooltipProps> = {
  bg: 'red.500',
  color: 'white',
  textShadow: '1px 1px 3px #00000055',
}

export const AdditionalPregnancyView = ({
  pregnancy,
  index,
  onEditClick,
  onDelete,
}: {
  pregnancy: FieldMapValue
  index: number
  onEditClick: () => void
  onDelete: () => Promise<ValidationErrors>
}) => {
  const {
    pregnancyOutcome,
    deliveryTypes,
    deliveryLocation,
    deliveryLocationOther,
    newbornFname,
    newbornLname,
    deliveredOn,
    lmp,
    newbornSex,
    newbornWeight,
    childsGestationAndBirth,
  } = pregnancy

  const [viewingMore, setViewingMore] = useState(false)
  const childGestationAndBirthRows = useChildGestationAndBirthRows(childsGestationAndBirth)
  const bg = getRowBg(index)

  // if no child gestation details, return 1
  // if less than 4 child gestation details, return 2, otherwise return 3

  const weightString = useMemo(() => {
    if (!newbornWeight) return 'None'
    const { lb, oz } = newbornWeight
    if (lb === undefined || isNaN(lb) || oz === undefined || isNaN(oz)) return 'None'
    return `${lb} lb, ${oz} oz`
  }, [newbornWeight])

  const locationString = useMemo(() => {
    if (!deliveryLocation) return 'None'
    if (deliveryLocation === 'other') return `Other - ${deliveryLocationOther}`
    return formatValue({ field: deliveryLocationField, value: deliveryLocation })
  }, [deliveryLocation, deliveryLocationOther])

  const nameString = useMemo(() => {
    if (!newbornFname && !newbornLname) return 'None'
    return `${newbornFname} ${newbornLname}`
  }, [newbornFname, newbornLname])
  return (
    <>
      <GridItem rowSpan={viewingMore ? childGestationAndBirthRows.length + 1 : 1}>
        <ExistingPregnancyDataLabel
          bg={bg}
          textProps={{ fontWeight: 600 }}
          value={`${index + 1}`}
        />
      </GridItem>
      <ExistingPregnancyDataLabel
        bg={bg}
        onViewMore={
          childGestationAndBirthRows.length ? () => setViewingMore(!viewingMore) : undefined
        }
        viewingMore={viewingMore}
        value={formatValue({ field: pregnancyOutcomeField, value: pregnancyOutcome })}
      />
      <ExistingPregnancyDataLabel
        bg={bg}
        value={formatValue({ field: deliveryTypesField, value: deliveryTypes })}
      />
      <ExistingPregnancyDataLabel bg={bg} value={locationString} />
      <ExistingPregnancyDataLabel bg={bg} value={nameString} />
      <ExistingPregnancyDataLabel
        bg={bg}
        value={deliveredOn ? getDateString(deliveredOn, 'short', true) : 'None'}
      />
      <ExistingPregnancyDataLabel
        bg={bg}
        value={lmp ? getDateString(lmp, 'short', true) : 'None'}
      />
      <ExistingPregnancyDataLabel
        bg={bg}
        value={formatValue({ field: newbornSexField, value: newbornSex })}
      />
      <ExistingPregnancyDataLabel bg={bg} value={weightString} />
      <GridItem rowSpan={viewingMore ? childGestationAndBirthRows.length + 1 : 1}>
        <Center gap={1.5} p={2} h="100%" w="100%" bg={bg}>
          <Tooltip
            bg="green.500"
            color="white"
            textShadow="1px 1px 3px #00000055"
            placement="top"
            hasArrow
            label="Edit">
            <IconButton aria-label="edit" colorScheme="green" size="sm" onClick={onEditClick}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <DeleteButton
            variant="solid"
            itemName="pregnancy"
            bg="red.500"
            _hover={{ bg: 'red.600' }}
            onDelete={onDelete}
            size="sm"
            tooltipProps={deleteTooltipProps}
            icon={<DeleteIcon color="white" />}
          />
        </Center>
      </GridItem>
      {viewingMore && childGestationAndBirthRows.length ? (
        <GridItem colSpan={previousPregnancyNumColumns - 2}>
          <ChildGestationAndBirthView bg={bg} rows={childGestationAndBirthRows} />
        </GridItem>
      ) : null}
    </>
  )
}

const PreviousPregnanciesHeaderLabel = ({
  label,
  bg: _bg = 'white',
}: {
  label: string
  bg?: string
}) => (
  <Box w="100%" h="100%" bg="gray.100">
    <Center
      // bg={_bg}
      // bg={`${colors.green.hex}37`}
      h="100%"
      w="100%"
      py={1}
      px={2}>
      <Text fontSize="sm" fontWeight={600} fontFamily="Open Sans">
        {label}
      </Text>
    </Center>
  </Box>
)

const PreviousPregnanciesHeader = ({ inEditModal = false }: { inEditModal?: boolean }) => (
  <>
    {!inEditModal ? <PreviousPregnanciesHeaderLabel label="#" /> : null}
    {!inEditModal ? <PreviousPregnanciesHeaderLabel label="Outcome" /> : null}
    <PreviousPregnanciesHeaderLabel label="Delivery Type(s)" />
    <PreviousPregnanciesHeaderLabel label="Place of Delivery" />
    <PreviousPregnanciesHeaderLabel label="Newborn Name" />
    <PreviousPregnanciesHeaderLabel label="Date of Birth" />
    <PreviousPregnanciesHeaderLabel label="LMP" />
    <PreviousPregnanciesHeaderLabel label="Newborn Sex" />
    <PreviousPregnanciesHeaderLabel label="Newborn Weight" />
    {!inEditModal ? <PreviousPregnanciesHeaderLabel bg="gray.50" label="Edit / Complete" /> : null}
  </>
)

type PregnancyItem =
  | { pregnancy: WithId<Assessment>; type: 'existing'; index: number }
  | { pregnancy: FieldMapValue; type: 'additional'; index: number }

const PreviousPregnanciesMobileView = ({
  pregnancies,
  onEditExisting,
  onEditAdditional,
  onDeleteAdditional,
  onAddPregnancy,
}: {
  pregnancies: PregnancyItem[]
  onEditExisting: (pregnancy: WithId<Assessment>) => void
  onEditAdditional: (index: number) => void
  onDeleteAdditional: (index: number) => Promise<ValidationErrors>
  onAddPregnancy: () => void
}) => (
  <VStack spacing={0} alignItems="stretch" w="100%">
    {pregnancies.length ? (
      pregnancies.map(({ type, pregnancy, index: typeIndex }, index) =>
        type === 'existing' ? (
          <MobileExistingPregnancyCard
            key={pregnancy.id}
            index={index}
            pregnancy={pregnancy}
            onEditClick={() => onEditExisting(pregnancy)}
          />
        ) : (
          <MobileAdditionalPregnancyCard
            key={index}
            index={index}
            pregnancy={pregnancy}
            onDelete={() => onDeleteAdditional(typeIndex)}
            onEditClick={() => onEditAdditional(typeIndex)}
          />
        ),
      )
    ) : (
      <Box border="1px solid #cdcdcd" borderRadius={4} bg="white" p={3} textAlign="center">
        <Text fontFamily="Open Sans" fontWeight={500}>
          No pregnancies
        </Text>
      </Box>
    )}
    <ActionButton
      mt={1}
      w="100%"
      leftIcon={<AddIcon />}
      size="sm"
      onClick={onAddPregnancy}
      bg="white">
      ADD PREGNANCY
    </ActionButton>
  </VStack>
)

const PreviousPregnanciesDesktopView = ({
  pregnancies,
  onEditExisting,
  onEditAdditional,
  onDeleteAdditional,
  onAddPregnancy,
}: {
  pregnancies: PregnancyItem[]
  onEditExisting: (pregnancy: WithId<Assessment>) => void
  onEditAdditional: (index: number) => void
  onDeleteAdditional: (index: number) => Promise<ValidationErrors>
  onAddPregnancy: () => void
}) => (
  <Grid
    border="1px solid #cdcdcd"
    borderRadius={4}
    w="100%"
    gap="1px"
    bg="#cdcdcd"
    overflow="hidden"
    templateColumns="26px repeat(9, minmax(0,1fr))">
    <PreviousPregnanciesHeader />
    {pregnancies.length ? (
      pregnancies.map(({ type, pregnancy, index: typeIndex }, index) =>
        type === 'existing' ? (
          <ExistingPregnancyView
            key={pregnancy.id}
            index={index}
            pregnancy={pregnancy}
            onEditClick={() => onEditExisting(pregnancy)}
          />
        ) : (
          <AdditionalPregnancyView
            key={index}
            index={index}
            pregnancy={pregnancy}
            onDelete={() => onDeleteAdditional(typeIndex)}
            onEditClick={() => onEditAdditional(typeIndex)}
          />
        ),
      )
    ) : (
      <GridItem colSpan={10}>
        <Text fontFamily="Open Sans" fontWeight={500}>
          No pregnancies
        </Text>
      </GridItem>
    )}
    <GridItem colSpan={10}>
      <Box w="100%" bg="gray.100" p={2}>
        <ActionButton leftIcon={<AddIcon />} size="sm" onClick={onAddPregnancy} bg="white">
          ADD PREGNANCY
        </ActionButton>
      </Box>
    </GridItem>
  </Grid>
)

// Mobile view components
const MobilePregnancyField = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Flex justifyContent="space-between" w="100%" py={1}>
    <Text fontSize="sm" fontFamily="Open Sans" fontWeight={600} color="gray.600">
      {label}:
    </Text>
    <Box fontSize="sm" fontFamily="Open Sans" textAlign="right" flex={1} ml={2}>
      {value ?? 'None'}
    </Box>
  </Flex>
)

const MobileExistingPregnancyCard = ({
  pregnancy,
  index,
  onEditClick,
}: {
  pregnancy: WithId<Assessment>
  index: number
  onEditClick: () => void
}) => {
  const { admin } = useGynCareView()
  const { delivery, data, corrections } = pregnancy
  const [viewingMore, setViewingMore] = useState(false)
  // const bg = getRowBg(index)
  const childsGestationAndBirth = useMemo(
    () =>
      admin
        ? mergeData(data?.childsGestationAndBirth, corrections?.childsGestationAndBirth)
        : data?.childsGestationAndBirth,
    [admin, corrections, data],
  )

  const pregnancyOutcome = useMemo(
    () =>
      admin ? (corrections?.pregnancyOutcome ?? data?.pregnancyOutcome) : data?.pregnancyOutcome,
    [admin, corrections, data],
  )
  const pregnancyOutcomeOther = useMemo(
    () =>
      admin
        ? (corrections?.pregnancyOutcomeOther ?? data?.pregnancyOutcomeOther)
        : data?.pregnancyOutcomeOther,
    [admin, corrections, data],
  )

  const childGestationAndBirthRows = useChildGestationAndBirthRows(childsGestationAndBirth)

  const lmp = corrections?.delivery?.lmp ?? data?.delivery?.lmp
  const {
    newbornFname,
    newbornLname,
    newbornSex,
    newbornWeight,
    deliveredOn,
    location,
    locationOther,
    deliveryTypes,
  } = delivery ?? {}

  const weightString = useMemo(() => {
    if (!newbornWeight) return 'None'
    const { lb, oz } = poundsToLbOz(newbornWeight)
    return `${lb} lb, ${oz} oz`
  }, [newbornWeight])

  const locationString = useMemo(() => {
    if (!location) return 'None'
    if (location === 'other') return `Other - ${locationOther}`
    return formatValue({ field: deliveryLocationField, value: location })
  }, [location, locationOther])

  const nameString = useMemo(() => {
    if (!newbornFname && !newbornLname) return 'None'
    return `${newbornFname} ${newbornLname}`
  }, [newbornFname, newbornLname])

  const formattedPregnancyOutcome = useMemo(() => {
    if (!pregnancyOutcome) return null
    if (pregnancyOutcome === 'other') return `Other - ${pregnancyOutcomeOther}`
    return formatValue({ field: pregnancyOutcomeField, value: pregnancyOutcome })
  }, [pregnancyOutcome, pregnancyOutcomeOther])

  return (
    <Box border="1px solid #cdcdcd" borderRadius={4} bg="white" p={3} w="100%" mb={2}>
      <Flex justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Text fontSize="md" fontFamily="Open Sans" fontWeight={600}>
          Pregnancy #{index + 1}
        </Text>
        {pregnancyOutcome ? (
          <ActionButton leftIcon={<EditIcon />} size="sm" onClick={onEditClick}>
            EDIT
          </ActionButton>
        ) : (
          <SolidActionButton size="sm" onClick={onEditClick}>
            COMPLETE
          </SolidActionButton>
        )}
      </Flex>
      <VStack spacing={2} alignItems="stretch" divider={<StackDivider borderColor="#cdcdcd" />}>
        <MobilePregnancyField
          label="Outcome"
          value={
            formattedPregnancyOutcome ? (
              formattedPregnancyOutcome
            ) : (
              <Text as="span" color="red.600" fontWeight={600}>
                Required
              </Text>
            )
          }
        />
        <MobilePregnancyField
          label="Delivery Type(s)"
          value={formatValue({ field: deliveryTypesField, value: deliveryTypes })}
        />
        <MobilePregnancyField label="Place of Delivery" value={locationString} />
        <MobilePregnancyField label="Newborn Name" value={nameString} />
        <MobilePregnancyField
          label="Date of Birth"
          value={deliveredOn ? getDateString(deliveredOn, 'short', true) : null}
        />
        <MobilePregnancyField label="LMP" value={lmp ? getDateString(lmp, 'short', true) : null} />
        <MobilePregnancyField
          label="Newborn Sex"
          value={formatValue({ field: newbornSexField, value: newbornSex })}
        />
        <MobilePregnancyField label="Newborn Weight" value={weightString} />
      </VStack>
      {childGestationAndBirthRows.length > 0 && (
        <Box mt={1}>
          <Button
            variant="link"
            fontSize="sm"
            w="100%"
            textAlign="left"
            justifyContent="flex-start"
            fontFamily="Open Sans"
            color="green.500"
            onClick={() => setViewingMore(!viewingMore)}>
            {viewingMore ? '- PREGNANCY DETAILS' : '+ PREGNANCY DETAILS'}
          </Button>
          {viewingMore && (
            <Box mt={2} pt={2} borderTop="1px solid #cdcdcd">
              <ChildGestationAndBirthView bg="white" rows={childGestationAndBirthRows} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

const MobileAdditionalPregnancyCard = ({
  pregnancy,
  index,
  onEditClick,
  onDelete,
}: {
  pregnancy: FieldMapValue
  index: number
  onEditClick: () => void
  onDelete: () => Promise<ValidationErrors>
}) => {
  const {
    pregnancyOutcome,
    deliveryTypes,
    deliveryLocation,
    deliveryLocationOther,
    newbornFname,
    newbornLname,
    deliveredOn,
    lmp,
    newbornSex,
    newbornWeight,
    childsGestationAndBirth,
  } = pregnancy

  const [viewingMore, setViewingMore] = useState(false)
  const childGestationAndBirthRows = useChildGestationAndBirthRows(childsGestationAndBirth)
  const bg = getRowBg(index)

  const weightString = useMemo(() => {
    if (!newbornWeight) return 'None'
    const { lb, oz } = newbornWeight
    if (lb === undefined || isNaN(lb) || oz === undefined || isNaN(oz)) return 'None'
    return `${lb} lb, ${oz} oz`
  }, [newbornWeight])

  const locationString = useMemo(() => {
    if (!deliveryLocation) return 'None'
    if (deliveryLocation === 'other') return `Other - ${deliveryLocationOther}`
    return formatValue({ field: deliveryLocationField, value: deliveryLocation })
  }, [deliveryLocation, deliveryLocationOther])

  const nameString = useMemo(() => {
    if (!newbornFname && !newbornLname) return 'None'
    return `${newbornFname} ${newbornLname}`
  }, [newbornFname, newbornLname])

  return (
    <Box border="1px solid #cdcdcd" borderRadius={4} bg={bg} p={3} w="100%" mb={2}>
      <Flex justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Text fontSize="md" fontFamily="Open Sans" fontWeight={600}>
          Pregnancy #{index + 1}
        </Text>
        <HStack spacing={2}>
          <Tooltip
            bg="green.500"
            color="white"
            textShadow="1px 1px 3px #00000055"
            placement="top"
            hasArrow
            label="Edit">
            <IconButton aria-label="edit" colorScheme="green" size="sm" onClick={onEditClick}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <DeleteButton
            variant="solid"
            itemName="pregnancy"
            bg="red.500"
            _hover={{ bg: 'red.600' }}
            onDelete={onDelete}
            size="sm"
            tooltipProps={deleteTooltipProps}
            icon={<DeleteIcon color="white" />}
          />
        </HStack>
      </Flex>
      <VStack spacing={2} alignItems="stretch" divider={<StackDivider borderColor="#cdcdcd" />}>
        <MobilePregnancyField
          label="Outcome"
          value={formatValue({ field: pregnancyOutcomeField, value: pregnancyOutcome })}
        />
        <MobilePregnancyField
          label="Delivery Type(s)"
          value={formatValue({ field: deliveryTypesField, value: deliveryTypes })}
        />
        <MobilePregnancyField label="Place of Delivery" value={locationString} />
        <MobilePregnancyField label="Newborn Name" value={nameString} />
        <MobilePregnancyField
          label="Date of Birth"
          value={deliveredOn ? getDateString(deliveredOn, 'short', true) : null}
        />
        <MobilePregnancyField label="LMP" value={lmp ? getDateString(lmp, 'short', true) : null} />
        <MobilePregnancyField
          label="Newborn Sex"
          value={formatValue({ field: newbornSexField, value: newbornSex })}
        />
        <MobilePregnancyField label="Newborn Weight" value={weightString} />
      </VStack>
      {childGestationAndBirthRows.length > 0 && (
        <Box mt={3}>
          <Button
            variant="link"
            fontSize="sm"
            w="100%"
            textAlign="left"
            justifyContent="flex-start"
            fontFamily="Open Sans"
            color="green.500"
            onClick={() => setViewingMore(!viewingMore)}>
            {viewingMore ? '- DETAILS' : '+ DETAILS'}
          </Button>
          {viewingMore && (
            <Box mt={2} pt={2} borderTop="1px solid #cdcdcd">
              <ChildGestationAndBirthView bg={bg} rows={childGestationAndBirthRows} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

const AdditionalPregnancyModal = ({
  isOpen,
  value,
  onClose,
  onSubmit,
}: {
  isOpen: boolean
  value: FieldMapValue | undefined
  onClose: () => void
  onSubmit: (values: FieldMapValue) => Promise<ValidationErrors>
}) => {
  const { height: screenHeight } = useScreen()
  const maxHeight = useMemo(() => Math.min(screenHeight * 0.8, 800), [screenHeight])
  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      overlayHeader
      size="3xl"
      bodyProps={{
        style: { overflow: 'hidden', flex: 'unset', height: 'auto', maxHeight: 'auto' },
      }}
      render={() => (
        <Flex flexFlow="column" h="auto" w="100%" maxH={`${maxHeight}px`}>
          <Flex bg="gray.50" w="100%" zIndex={2} px={3} py={3} borderBottom="1px solid #cdcdcd">
            <Text fontSize="lg" fontWeight={500} fontFamily="Hero-New">
              Add Additional Pregnancy
            </Text>
          </Flex>
          <Box flex={1} minH={0} overflowY="auto">
            <SimpleForm
              field={additionalPregnanciesField.itemFields}
              boxProps={{ ...overflowBody.boxProps, bg: 'gray.100' }}
              footerFlexProps={overflowBody.footerFlexProps}
              value={value}
              canSubmitClean
              submitText="SAVE"
              submitCleanText="SAVE"
              onSubmit={onSubmit}
            />
          </Box>
        </Flex>
      )}
    />
  )
}

const MobileExistingPregnancyModalView = ({
  pregnancy,
  admin: _admin,
}: {
  pregnancy: WithId<Assessment>
  admin: boolean
}) => {
  const { delivery, data, corrections } = pregnancy
  const lmp = corrections?.delivery?.lmp ?? data?.delivery?.lmp
  const {
    newbornFname,
    newbornLname,
    newbornSex,
    newbornWeight,
    deliveredOn,
    location,
    locationOther,
    deliveryTypes,
  } = delivery ?? {}

  const weightString = useMemo(() => {
    if (!newbornWeight) return 'None'
    const { lb, oz } = poundsToLbOz(newbornWeight)
    return `${lb} lb, ${oz} oz`
  }, [newbornWeight])

  const locationString = useMemo(() => {
    if (!location) return 'None'
    if (location === 'other') return `Other - ${locationOther}`
    return formatValue({ field: deliveryLocationField, value: location })
  }, [location, locationOther])

  const nameString = useMemo(() => {
    if (!newbornFname && !newbornLname) return 'None'
    return `${newbornFname} ${newbornLname}`
  }, [newbornFname, newbornLname])

  return (
    <Box border="1px solid #cdcdcd" borderRadius={4} bg="white" p={3} w="100%">
      <VStack spacing={2} alignItems="stretch" divider={<StackDivider borderColor="#cdcdcd" />}>
        <MobilePregnancyField
          label="Delivery Type(s)"
          value={formatValue({ field: deliveryTypesField, value: deliveryTypes })}
        />
        <MobilePregnancyField label="Place of Delivery" value={locationString} />
        <MobilePregnancyField label="Newborn Name" value={nameString} />
        <MobilePregnancyField
          label="Date of Birth"
          value={deliveredOn ? getDateString(deliveredOn, 'short', true) : null}
        />
        <MobilePregnancyField label="LMP" value={lmp ? getDateString(lmp, 'short', true) : null} />
        <MobilePregnancyField
          label="Newborn Sex"
          value={formatValue({ field: newbornSexField, value: newbornSex })}
        />
        <MobilePregnancyField label="Newborn Weight" value={weightString} />
      </VStack>
    </Box>
  )
}

const ExistingPregnancyModal = ({
  isOpen,
  value,
  onClose,
  onSubmit,
  admin,
}: {
  isOpen: boolean
  value: WithId<Assessment>
  onClose: () => void
  onSubmit: (values: Assessment) => Promise<ValidationErrors>
  admin: boolean
}) => {
  const data = useMemo(
    () => (admin ? mergeData(value.data ?? {}, value.corrections ?? {}) : value.data),
    [value, admin],
  )

  const { height: screenHeight, isMobile } = useScreen()
  const maxHeight = useMemo(() => Math.min(screenHeight * 0.8, 800), [screenHeight])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      overlayHeader
      bodyProps={{
        style: { overflow: 'hidden', flex: 'unset', height: 'auto', maxHeight: 'auto' },
      }}
      size="3xl"
      render={() => (
        <Flex flexFlow="column" h="auto" w="100%" maxH={`${maxHeight}px`}>
          <Flex bg="gray.50" w="100%" px={3} zIndex={2} py={3} borderBottom="1px solid #cdcdcd">
            <Text fontSize="lg" fontWeight={500} fontFamily="Hero-New">
              Complete Existing Pregnancy
            </Text>
          </Flex>
          <Box flex={1} minH={0} overflowY="auto">
            {isMobile ? (
              <MobileExistingPregnancyModalView pregnancy={value} admin={admin} />
            ) : (
              <Grid
                border="1px solid #cdcdcd"
                borderRadius={4}
                w="100%"
                gap="1px"
                bg="#cdcdcd"
                overflow="hidden"
                templateColumns="repeat(7, minmax(0,1fr))">
                <PreviousPregnanciesHeader inEditModal />
                <ExistingPregnancyView pregnancy={value} index={0} inEditModal />
              </Grid>
            )}
            <SimpleForm
              field={existingPregnancyField}
              boxProps={{ bg: 'gray.100' }}
              value={data}
              canSubmitClean
              submitCleanText="SAVE"
              submitText="SAVE"
              onSubmit={onSubmit}
            />
          </Box>
        </Flex>
      )}
    />
  )
}
export const PreviousPregnanciesInput = ({
  onSubmit,
}: {
  onSubmit: RenderStageProps['onSubmit']
}) => {
  const { admin, formData } = useGynCareView()
  const { assessmentsArr } = useMyAssessments()
  const { values } = useFormState({ subscription: { values: true } })
  const { isMobile } = useScreen()
  const additionalPregnancies = useMemo(() => {
    const additionalPregnancies = formData?.gynAndSexualHistory?.gynHistory?.additionalPregnancies
    if (!additionalPregnancies || !Array.isArray(additionalPregnancies)) return []
    return additionalPregnancies as FieldMapValue[]
  }, [formData])
  const existingPregnancies = useMemo(() => {
    const supplementalData = (values?.gynHistory?.existingPregnancies ?? {}) as Record<
      string,
      FieldMapValue
    >
    return assessmentsArr.map(assessment =>
      admin
        ? {
            ...assessment,
            corrections: {
              ...assessment.corrections,
              ...supplementalData[assessment.id],
            },
          }
        : {
            ...assessment,
            data: {
              ...assessment.data,
              ...supplementalData[assessment.id],
            },
          },
    )
  }, [values, assessmentsArr, admin])

  const pregnancies = useMemo(
    () =>
      (
        [
          ...existingPregnancies.map((p, index) => ({
            pregnancy: p,
            type: 'existing',
            index,
          })),
          ...additionalPregnancies.map((p, index) => ({
            pregnancy: p,
            type: 'additional',
            index,
          })),
        ] as Array<
          | { pregnancy: WithId<Assessment>; type: 'existing'; index: number }
          | { pregnancy: FieldMapValue; type: 'additional'; index: number }
        >
      ).sort((a, b) => {
        const aDeliveryDate =
          a.type === 'existing'
            ? (a.pregnancy.delivery?.deliveredOn ??
              a.pregnancy.corrections?.delivery?.deliveredOn ??
              a.pregnancy.data?.delivery?.deliveredOn ??
              null)
            : (a.pregnancy.deliveredOn ?? null)
        if (!aDeliveryDate || isNaN(aDeliveryDate)) return 1
        const bDeliveryDate =
          b.type === 'existing'
            ? (b.pregnancy.delivery?.deliveredOn ??
              b.pregnancy.corrections?.delivery?.deliveredOn ??
              b.pregnancy.data?.delivery?.deliveredOn ??
              null)
            : (b.pregnancy.deliveredOn ?? null)
        if (!bDeliveryDate || isNaN(bDeliveryDate)) return -1
        return aDeliveryDate - bDeliveryDate
      }),
    [existingPregnancies, additionalPregnancies],
  )

  const [addingPregnancy, setAddingPregnancy] = useState(false)
  const [editingAdditionalPregnancy, setEditingAdditionalPregnancy] = useState<number | null>(null)
  const [editingExistingPregnancy, setEditingExistingPregnancy] =
    useState<WithId<Assessment> | null>(null)

  const handleSubmit = useCallback(
    async (values: FieldMapValue) => onSubmit(values, () => {}, undefined, false),
    [onSubmit],
  )

  const handleDeleteAdditional = async (index: number): Promise<ValidationErrors> => {
    const newValues = [...additionalPregnancies]
    newValues.splice(index, 1)
    return handleSubmit({ gynHistory: { additionalPregnancies: newValues } })
  }

  return (
    <Box w="100%" pb={3} px={isMobile ? 1 : 2}>
      <Text fontFamily="Open Sans" fontWeight={500} fontSize="lg" mb={1}>
        Previous Pregnancies
      </Text>
      <Text fontFamily="Open Sans" fontSize="md" mb={1}>
        Please indicate below any prior pregnancies, regardless of outcomes (living, stillborn,
        miscarriage, abortion, etc.)
      </Text>
      {isMobile ? (
        <PreviousPregnanciesMobileView
          pregnancies={pregnancies}
          onEditExisting={pregnancy => setEditingExistingPregnancy(pregnancy)}
          onEditAdditional={index => setEditingAdditionalPregnancy(index)}
          onDeleteAdditional={handleDeleteAdditional}
          onAddPregnancy={() => setAddingPregnancy(true)}
        />
      ) : (
        <PreviousPregnanciesDesktopView
          pregnancies={pregnancies}
          onEditExisting={pregnancy => setEditingExistingPregnancy(pregnancy)}
          onEditAdditional={index => setEditingAdditionalPregnancy(index)}
          onDeleteAdditional={handleDeleteAdditional}
          onAddPregnancy={() => setAddingPregnancy(true)}
        />
      )}
      {editingAdditionalPregnancy !== null || addingPregnancy ? (
        <AdditionalPregnancyModal
          isOpen={addingPregnancy || editingAdditionalPregnancy !== null}
          value={
            editingAdditionalPregnancy !== null
              ? additionalPregnancies[editingAdditionalPregnancy]
              : undefined
          }
          onClose={() => {
            setAddingPregnancy(false)
            setEditingAdditionalPregnancy(null)
          }}
          onSubmit={async values => {
            const newValues =
              editingAdditionalPregnancy !== null
                ? [...additionalPregnancies]
                : [...additionalPregnancies, values]
            if (editingAdditionalPregnancy !== null) {
              newValues[editingAdditionalPregnancy] = values
            }
            // change('gynHistory.additionalPregnancies', newValues)
            const res = await handleSubmit({ gynHistory: { additionalPregnancies: newValues } })
            if (!res) {
              setAddingPregnancy(false)
              setEditingAdditionalPregnancy(null)
            }
            return res
          }}
        />
      ) : null}
      {editingExistingPregnancy ? (
        <ExistingPregnancyModal
          isOpen={true}
          value={editingExistingPregnancy}
          onClose={() => setEditingExistingPregnancy(null)}
          admin={admin}
          onSubmit={async values => {
            const res = await handleSubmit({
              gynHistory: { existingPregnancies: { [editingExistingPregnancy.id]: values } },
            })

            if (!res) setEditingExistingPregnancy(null)
            return res
          }}
        />
      ) : null}
    </Box>
  )
}
